"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Calculator, HeartPulse, Wrench, Zap, Scale, ShoppingBag, Briefcase, Building2, ArrowRight, CircleCheck as CheckCircle2, Sparkles } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const sectorsList = [{
  id: "accountants",
  title: "Accountants & Tax Advisors",
  description: "Attract high-value business owners seeking tax advice, payroll management, and year-end accounting services.",
  highlight: "B2B Lead Generation",
  icon: "Calculator"
}, {
  id: "dentists",
  title: "Dentists & Dental Clinics",
  description: "Dominate local maps for high-ticket dental procedures like Invisalign, implants, and cosmetic dentistry.",
  highlight: "Local Maps & High Intent",
  icon: "HeartPulse"
}, {
  id: "solicitors",
  title: "Solicitors & Law Firms",
  description: "Establish strong search authority for specialized legal services, conveyancing, and corporate litigation.",
  highlight: "High-Authority Content",
  icon: "Scale"
}, {
  id: "plumbers",
  title: "Plumbers & Heating Engineers",
  description: "Capture urgent local service searches and recurring boiler maintenance contracts across your operational radius.",
  highlight: "Emergency & Local SEO",
  icon: "Wrench"
}, {
  id: "electricians",
  title: "Electricians & Electrical Contractors",
  description: "Rank for both domestic emergency calls and lucrative commercial electrical installation contracts.",
  highlight: "Radius & Commercial Search",
  icon: "Zap"
}, {
  id: "ecommerce",
  title: "Ecommerce & Online Retail",
  description: "Optimize category architecture, product schema, and faceted navigation to boost organic sales revenue.",
  highlight: "Technical & Schema SEO",
  icon: "ShoppingBag"
}, {
  id: "b2b",
  title: "B2B & Professional Services",
  description: "Position your consultancy or SaaS solution directly in front of corporate decision-makers during research phases.",
  highlight: "Pipeline & Funnel SEO",
  icon: "Briefcase"
}, {
  id: "realestate",
  title: "Estate Agents & Property Services",
  description: "Win localized search queries for home valuations, lettings management, and commercial property listings.",
  highlight: "Hyper-Local Authority",
  icon: "Building2"
}];
export default function Sectors() {
  const shouldReduceMotion = useReducedMotion();
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case "Calculator":
        return <Calculator className="size-6 text-primary" />;
      case "HeartPulse":
        return <HeartPulse className="size-6 text-primary" />;
      case "Scale":
        return <Scale className="size-6 text-primary" />;
      case "Wrench":
        return <Wrench className="size-6 text-primary" />;
      case "Zap":
        return <Zap className="size-6 text-primary" />;
      case "ShoppingBag":
        return <ShoppingBag className="size-6 text-primary" />;
      case "Briefcase":
        return <Briefcase className="size-6 text-primary" />;
      case "Building2":
        return <Building2 className="size-6 text-primary" />;
      default:
        return <Briefcase className="size-6 text-primary" />;
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="industrysectorsgrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-300">
      <div className="max-w-7xl mx-auto space-y-16">
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider gap-1.5">
            <Sparkles className="size-3.5 text-primary" />
            Tailored Industry Expertise
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold font-serif tracking-tight text-foreground">
            Bespoke SEO Strategies for Key UK Sectors
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground font-default leading-relaxed">
            Every industry has unique search behaviors, keyword intents, and competitive dynamics. I apply tailored, hands-on search engine optimization strategies that deliver qualified leads and sustainable organic revenue for your specific market.
          </p>
        </div>

        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {sectorsList.map((sector, i) => <div data-index={i} key={sector.id} className="h-full">
              <motion.div variants={cardVariants} className="h-full">
                <Card className="h-full bg-card border border-border/80 hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-300 flex flex-col justify-between rounded-xl overflow-hidden group">
                  <CardHeader className="space-y-4 pt-6">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-lg bg-primary/10 border border-primary/15 transition-colors duration-300 group-hover:bg-primary/20">
                        {getIcon(sector.icon)}
                      </div>
                      <Badge variant="outline" className="text-[13px] font-medium text-muted-foreground border-border bg-background">
                        {sector.highlight}
                      </Badge>
                    </div>
                    <CardTitle className="text-lg font-semibold font-serif text-card-foreground group-hover:text-primary transition-colors duration-200">
                      {sector.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4 flex-1 flex flex-col justify-between">
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed font-default">
                      {sector.description}
                    </CardDescription>
                    <div className="pt-2 flex items-center text-xs font-semibold text-primary gap-1 group-hover:translate-x-1 transition-transform duration-200">
                      <span>Direct Expert Access</span>
                      <CheckCircle2 className="size-3.5 ml-auto text-primary" />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>)}
        </motion.div>

        <div className="rounded-2xl bg-muted/50 border border-border p-8 md:p-10 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xs">
          <div className="space-y-2 text-center md:text-left max-w-2xl">
            <h3 className="text-xl sm:text-2xl font-bold font-serif text-foreground">
              Don’t see your specific industry listed?
            </h3>
            <p className="text-sm sm:text-base text-muted-foreground">
              I apply custom technical, local, and national search frameworks to businesses across all specialized sectors in the UK and internationally.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="font-medium shadow-sm cursor-pointer">
              <Link to="/ContactPage">
                Book a Free Consultation
                <ArrowRight className="size-4 ml-1" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="font-medium cursor-pointer">
              <Link to="/CaseStudiesPage">
                Explore Case Studies
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}